import React from 'react';
import { useState, useEffect } from 'react';
import { Appointment } from '../api/appointment';
import { authenticatedFetch } from '../utils/api';
import { Calendar, Clock, AlertCircle, CalendarX, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AppointmentModal } from './appointment-modal';
import { getUserTimezone } from '../utils/date';

const fetchUserAppointments = async (): Promise<Appointment[]> => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/api/appointments/user`);

  if (!response.ok) {
    throw new Error('Failed to fetch appointments');
  }

  return response.json();
};

const formatDate = (date: string, tz: string): string => {
  return new Date(date).toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: tz
  });
};

const formatTime = (date: string, tz: string): string => {
  return new Date(date).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: tz
  });
};

const cancelAppointment = async (appointmentId: string): Promise<void> => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/api/appointments/${appointmentId}/cancel`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to cancel appointment');
  }
};

const AppointmentItem: React.FC<{
  appointment: Appointment;
  isPast?: boolean;
  onCancel: () => void;
  onReschedule?: () => void;
  isLast?: boolean;
}> = ({ appointment, isPast = false, onCancel, onReschedule, isLast }) => {
  const [isCancelling, setIsCancelling] = useState(false);
  const tz = getUserTimezone();

  const handleCancel = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler ce rendez-vous ?')) {
      return;
    }

    try {
      setIsCancelling(true);
      await cancelAppointment(appointment.id);
      onCancel();
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      alert(err instanceof Error ? err.message : 'Erreur lors de l\'annulation');
    } finally {
      setIsCancelling(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'scheduled':
        if (isPast) return <Badge variant="destructive">Annulé</Badge>;
        return <Badge className="bg-blue-500 hover:bg-blue-600">À venir</Badge>;
      case 'completed':
        return <Badge className="bg-green-500 hover:bg-green-600">Terminé</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Annulé</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className={`p-6 ${!isLast ? 'border-b border-gray-100' : ''} hover:bg-gray-50/50 transition-colors`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="font-bold text-lg text-gray-900">{appointment.therapist.name}</h3>
            {getStatusBadge(appointment.status)}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-gray-600">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#00c4cc]" />
              <span className="capitalize">{formatDate(appointment.date, tz)}</span>
            </div>
            <div className="hidden sm:block text-gray-300">|</div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-[#00c4cc]" />
              <span>{formatTime(appointment.date, tz)}</span>
            </div>
          </div>
        </div>

        {!isPast && appointment.status === 'scheduled' && (
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Button
              variant="outline"
              size="sm"
              className="w-full md:w-auto"
              onClick={onReschedule}
            >
              Modifier
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full md:w-auto text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleCancel}
              disabled={isCancelling}
            >
              {isCancelling ? 'Annulation...' : 'Annuler'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const AppointmentsList = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reschedulingAppointment, setReschedulingAppointment] = useState<Appointment | null>(null);

  const loadAppointments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchUserAppointments();
      setAppointments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load appointments');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAppointments();
  }, []);

  const now = new Date();
  const upcomingAppointments = appointments.filter(
    app => new Date(app.date) > now && app.status !== 'cancelled'
  ).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const pastAppointments = appointments.filter(
    app => new Date(app.date) <= now || app.status === 'cancelled'
  ).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00c4cc]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 pb-12">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-[#00c4cc] to-[#008ba3] text-white pt-10 pb-24 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold">Mes rendez-vous</h1>
          <p className="text-white/80 mt-2">Consultez et gérez vos consultations à venir et passées</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 -mt-10">
        {error && (
          <Alert variant="destructive" className="mb-6 shadow-md bg-white">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-8">
          <section>
            <Card className="shadow-lg border-none overflow-hidden">
              <div className="p-6 border-b border-gray-100 bg-white">
                <h2 className="text-xl font-bold flex items-center gap-2 text-gray-800">
                  <Calendar className="h-5 w-5 text-[#00c4cc]" />
                  Prochains rendez-vous
                </h2>
              </div>
              <div>
                {upcomingAppointments.length === 0 ? (
                  <div className="py-12 text-center">
                    <div className="p-4 bg-gray-50 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <CalendarX className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="font-semibold text-gray-900">Aucun rendez-vous à venir</h3>
                    <p className="text-gray-500 mt-1 max-w-xs mx-auto mb-6">Vous n'avez pas de consultations programmées pour le moment.</p>
                    <a href="/therapists" className="text-[#00c4cc] hover:underline">
                      Trouver un spécialiste
                    </a>
                  </div>
                ) : (
                  upcomingAppointments.map((appointment, index) => (
                    <AppointmentItem
                      key={appointment.id}
                      appointment={appointment}
                      onCancel={loadAppointments}
                      onReschedule={() => setReschedulingAppointment(appointment)}
                      isLast={index === upcomingAppointments.length - 1}
                    />
                  ))
                )}
              </div>
            </Card>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-gray-800 px-1">
              <CheckCircle2 className="h-5 w-5 text-gray-400" />
              Historique
            </h2>
            <Card className="shadow-sm overflow-hidden">
              {pastAppointments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Aucun historique de rendez-vous</p>
                </div>
              ) : (
                pastAppointments.map((appointment, index) => (
                  <AppointmentItem
                    key={appointment.id}
                    appointment={appointment}
                    isPast
                    onCancel={loadAppointments}
                    isLast={index === pastAppointments.length - 1}
                  />
                ))
              )}
            </Card>
          </section>
        </div>
      </div>

      {reschedulingAppointment && (
        <AppointmentModal
          isOpen={!!reschedulingAppointment}
          onClose={() => {
            setReschedulingAppointment(null);
            loadAppointments();
          }}
          therapist={reschedulingAppointment.therapist}
          existingAppointment={reschedulingAppointment}
        />
      )}
    </div>
  );
};
